// @flow
import * as React from 'react';
import { Container, Col, Row } from 'reactstrap';
import parse from 'date-fns/parse';
import classNames from 'classnames';
import qs from 'store/entities/QueryString';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';
import 'react-image-lazy-load-component/lib/style.css';

// Components
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowHeader from 'components/molecules/AppointmentFlowHeader';
import RemindersAndUpdates from 'components/molecules/RemindersAndUpdates';
import AdditionalQuestions from 'components/molecules/AdditionalQuestions';
import ContactInfo from 'components/molecules/ContactInfo';
import Media from 'react-media';
import BookItButton from 'components/molecules/BookItButton';
import Panel from 'components/atoms/Panel';
import { Icon } from 'components/atoms/Icon/';
import Modal from 'components/atoms/Modal';
import Button from 'components/atoms/Button';
import Price from 'components/atoms/Price';
import AppointmentError from 'components/api/components/AppointmentNotAvailable/AppointmentError';
import PromoCode from './components/PromoCode';
import Disclaimer from 'components/atoms/Disclaimer';
import VehiclePanel from 'components/atoms/VehiclePanel';
import CTAFlowHeader from 'components/molecules/CTAFlowHeader';
import InvisibleRecaptcha from 'components/molecules/InvisibleRecaptcha';
import MessageBanner from 'components/atoms/MessageBanner';

// Utils
import { setTransportationLabel } from 'utils/transportationLabel';
import { isDiscountValid } from 'utils/promotions';
import { formatTime } from 'utils/date';
import { getCTAbackButtonLabel, toCapitalize } from 'utils/string';
import { isMobile, resetScrollPosition } from 'utils/DOM';

// Constants
import { NAVIGATION } from 'constants/navigations';
import {
  APPOINTMENT_ROUTE,
  CANCELLATION_ROUTE,
  REVIEW_ROUTE,
  USER_VEHICLES_ROUTE,
  HOME_ROUTE
} from 'constants/routes';
import { APP_BREAKPOINTS } from 'constants/appConfig';
import { TRANSPORTATION } from 'constants/transportation';
import {
  assignADA,
  REVIEW_PAGE_ADA,
  REVIEW_PAGE_TAB_ELEMENTS,
  getTabElements
} from 'utils/adaConfigurations/ReviewPageADA';
import { APP_FLOW } from 'constants/appFlow';
import { GTM_API, GTM_RECAPTCHA_EVENTS } from 'constants/recaptcha';

// Types
import { gtmUserEventType } from 'types/gtm';
import { type Promotion } from 'api/xws/promotions/promotionsWs';
import { type TotalPrice } from 'types/price';
import type { CTA } from 'api/xws/optimizely/optimizely';

import 'components/pages/components/ContentFooter/components/TermsPrivacy/TermsPrivacy.scss';
import './ReviewPage.scss';

const { FORWARD, BACKWARD } = NAVIGATION;
const { VALET, VALETLOANER } = TRANSPORTATION;
const { QUICK_APPT } = APP_FLOW;
const { cancel } = GTM_API;

const ns = 'review-page';
const modalView = qs.get('vmode') === 'modal';

export type Props = {
  goTo: (path: string, direction: string) => void,
  gtmUserEvent: gtmUserEventType,
  servicePoint: Object,
  services: Array<Object>,
  vehicle: Object,
  isContactInfoFormValid: boolean,
  isFormRemindersValid: boolean,
  isLicenseNumberValid: boolean,
  appointment: Object,
  selectedAdvisorId: number,
  transportationOption: string,
  advisors: Array<Object>,
  transportations: Array<Object>,
  appointmentDate: Object,
  intl: IntlShape,
  isModifyEnabled: boolean,
  isPromotionEnabled: boolean,
  isApptInPromoInterval: boolean,
  cancelAppt: () => void,
  declinedServices: Array<Object>,
  totalPrice: TotalPrice,
  notes?: string,
  setActivePromotion: (promo: Promotion | null) => void,
  promoDescription: string,
  privacySettings: Object,
  priceVisibility: boolean,
  servicesCount: number,
  contactInfoMissing: boolean,
  mobilePhoneNumberPopulated: () => void,
  showMileage: boolean,
  isPromotionInRange: boolean,
  isFlexPayEnabled?: boolean,
  externalValet: string,
  appFlow: string,
  ctaBanner: CTA | null,
  ctaBackButton: string | null,
  isCarryOver: boolean,
  isDmsAppt: boolean,
  isQuoteAppt: boolean,
  isLoggedIn: boolean,
  pickupAddress: string,
  dropoffAddress: string,
  dealerContact: Object,
  isClutchPartialValet: boolean,
  setRecaptchaToken: string => void,
  isRecaptchaEnabledOnCancelAppt: boolean,
  languageCode: string,
  gtmRecaptchaEvent: (event: string, api: string, token: string) => void,
  smsSingleOptIn: boolean
};

type State = {
  activeRunning: boolean,
  activeSuccessAnimation: boolean,
  contactInfoActivateAnimation: boolean,
  currentTab: number,
  totalTabElements: number,
  pageDidTab: boolean
};

type Ref = {
  current: any
};

class ReviewPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    if (props.isRecaptchaEnabledOnCancelAppt) {
      this.recaptcha = React.createRef();
    }
    (this: any).promoCodeModal = React.createRef();
    (this: any).showPromoCodeModal = this.showPromoCodeModal.bind(this);
  }

  state = {
    activeRunning: false,
    activeSuccessAnimation: false,
    contactInfoActivateAnimation: false,
    currentTab: 0,
    totalTabElements: 0,
    pageDidTab: false,
    isListeningForRecaptcha: false
  };

  componentDidMount() {
    resetScrollPosition(0);
    const root = document.getElementById('root');
    root && root.addEventListener('keydown', this.handleKeyDown, true);
    root && root.addEventListener('click', this.handleClick, true);
    const { intl, appFlow } = this.props;
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      totalTabElements: getTabElements(appFlow).length
    });
    assignADA(REVIEW_PAGE_ADA, intl);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.contactInfoMissing !== prevProps.contactInfoMissing) {
      const { intl, appFlow } = this.props;
      /* eslint-disable react/no-did-update-set-state */
      this.setState({
        totalTabElements: getTabElements(appFlow).length,
        currentTab: 0,
        pageDidTab: false
      });
      /* eslint-disable react/no-did-update-set-state */
      assignADA(REVIEW_PAGE_ADA, intl);
    }
  }

  cancelApptmodal: Ref = React.createRef();
  errorBooking: Ref = React.createRef();
  headerFlow: Ref = React.createRef();
  promoCodeModal: React.Ref<any>;

  handleClick = (e: Object) => {
    if (window.location.href.indexOf(REVIEW_ROUTE) === -1) {
      return;
    }
    const className = e.target.className;
    let index = -1;

    REVIEW_PAGE_TAB_ELEMENTS.forEach(function(element, elementIndex) {
      if (className.indexOf(element) !== -1) {
        index = elementIndex;
      }
    });

    if (index !== -1) {
      this.setState({
        currentTab: index + 1 - +this.state.pageDidTab
      });
    }
  };

  handleKeyDown = (e: Object) => {
    if (window.location.href.indexOf(REVIEW_ROUTE) === -1) {
      return;
    }
    if (e.shiftKey && e.keyCode === 9) {
      e.preventDefault();
      if (this.state.pageDidTab) {
        this.setState({
          currentTab:
            (this.state.currentTab - 1 + this.state.totalTabElements) %
            this.state.totalTabElements
        });
      } else {
        this.setState({
          currentTab: this.state.currentTab - 2
        });
      }
      this.goNextTabElement();
      this.setState({
        pageDidTab: true
      });
    } else if (e.keyCode === 9) {
      e.preventDefault();
      if (this.state.pageDidTab) {
        this.setState({
          currentTab: (this.state.currentTab + 1) % this.state.totalTabElements
        });
      }
      this.goNextTabElement();
      this.setState({
        pageDidTab: true
      });
    }
  };

  goNextTabElement = () => {
    const currentTab = REVIEW_PAGE_TAB_ELEMENTS[this.state.currentTab];
    const htmlTag = document.getElementsByClassName(currentTab)[0];
    if (htmlTag) {
      htmlTag.focus();
      !modalView && htmlTag.scrollIntoView();
    }
  };

  showPromoCodeModal = (show: boolean = true) => {
    (this: any).promoCodeModal.current.setShowModal(show);
  };

  openModal = () => {
    this.cancelApptmodal.current.setShowModal(true);
    this.gtmCancelAppointmentModal();
  };

  throwErrorBooking = () => {
    const { gtmUserEvent } = this.props;
    this.errorBooking.current.setShowModal(true);
    // GTM ERROR BOOKING
    gtmUserEvent(
      'Pop Up Error',
      'Error Message',
      'Pop Up Error Show Up',
      'GTM_CURRENT_PAGE'
    );
  };

  closeModal = () => {
    this.cancelApptmodal.current.setShowModal(false);
  };

  gtmCancelAppointmentModal() {
    const { gtmUserEvent, isModifyEnabled } = this.props;
    if (isModifyEnabled) {
      gtmUserEvent(
        'Cancel Appointment Button',
        'Clicked',
        'Open Cancel Appointment Modal',
        'GTM_CURRENT_PAGE > Cancel Appointment Modal'
      );
    }
  }

  onCancelAppt = async () => {
    const {
      gtmUserEvent,
      cancelAppt,
      goTo,
      gtmRecaptchaEvent,
      recaptchaToken,
      isRecaptchaEnabledOnCancelAppt
    } = this.props;
    try {
      const success = await cancelAppt();
      gtmUserEvent(
        'Cancel Appointment',
        'Clicked',
        'Appointment Canceled',
        'GTM_CURRENT_PAGE > Cancel Appointment Modal > Cancel Appointment Button'
      );
      isRecaptchaEnabledOnCancelAppt &&
        gtmRecaptchaEvent(
          GTM_RECAPTCHA_EVENTS.API_CALL,
          cancel,
          recaptchaToken,
          success
        );
      goTo(CANCELLATION_ROUTE, FORWARD);
    } catch (err) {
      /* eslint-disable no-console */
      console.error(err);
      /* eslint-enable no-console */
    }
  };

  async cancelApptHandler() {
    const { isRecaptchaEnabledOnCancelAppt } = this.props;
    const { isListeningForRecaptcha } = this.state;
    document.body && document.body.classList.remove('modal-open');
    if (isRecaptchaEnabledOnCancelAppt) {
      !isListeningForRecaptcha &&
        this.recaptcha.current.initRecaptchaListener();
      this.setState({ isListeningForRecaptcha: true });
      this.recaptcha.current.execute();
    } else {
      await this.onCancelAppt();
    }
  }

  buildCancelApptmodal() {
    const { intl, appointmentDate } = this.props;
    const modalNS = 'cancel-appointment-modal';
    const date = parse(appointmentDate.startTime);
    return (
      <Modal ref={this.cancelApptmodal} size={'medium'} showCloseButton={true}>
        <div className={`${modalNS} d-flex flex-column justify-content-center`}>
          <div className={`${modalNS}__label`}>
            {intl.formatMessage(
              {
                id:
                  'c8.review.modal.cancel.are.you.sure.you.want.to.cancel.your.appointment'
              },
              {
                date: intl.formatDate(date, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })
              }
            )}
          </div>
          <Button
            className={`${modalNS}__cancel-button align-self-center`}
            onClick={() => this.cancelApptHandler()}
          >
            {intl.formatMessage({
              id: 'c8.review.modal.cancel.yes.cancel.it'
            })}
          </Button>
          <Button
            buttonTheme="link"
            className={`${modalNS}__never-mind-button`}
            onClick={this.closeModal}
          >
            {intl.formatMessage({
              id: 'c8.review.modal.cancel.never.mind.keep.my.appointment'
            })}
          </Button>
        </div>
      </Modal>
    );
  }

  buildPromoCodeModal = () => {
    return (
      <Modal ref={this.promoCodeModal}>
        <PromoCode
          closePromoCodeModal={() => {
            this.showPromoCodeModal(false);
          }}
        />
      </Modal>
    );
  };

  getValetAddressView = (
    label: string,
    address: string,
    pickupDate: string
  ) => {
    const firstPartPos = address.indexOf(',');
    const mainAddress = address.substring(0, firstPartPos);
    const remainingAddress = address.substring(
      firstPartPos,
      address.length - 1
    );

    return (
      <Row className={`${ns}__valet_info ${ns} mx-0`}>
        <Col xs={12} sm={12}>
          <div className={`${ns}__info-text`}>{label}</div>
          <div className={`${ns}__valet_info__address-container`}>
            <Icon
              type="location"
              className={`${ns}__valet_info__icon-location`}
            />
            <div
              className={`${ns}__valet_info__address ${ns}__valet_info__address--main`}
            >
              {mainAddress}
            </div>
            <div className={`${ns}__valet_info__address`}>
              {remainingAddress}
            </div>
          </div>
          <div className={`${ns}__valet_info__date`}>{pickupDate}</div>
        </Col>
      </Row>
    );
  };

  getServicesTitle() {
    const { servicePoint, servicesCount, intl, showMileage } = this.props;
    const mileage =
      servicePoint && showMileage
        ? `${(servicePoint.mileage / 1000).toFixed(1)}K `
        : '';
    const title = [
      servicesCount
        ? servicesCount > 1
          ? intl.formatMessage(
              {
                id: 'c8.common.service.plural'
              },
              { servicesCount }
            )
          : intl.formatMessage(
              {
                id: 'c8.common.service.singular'
              },
              { servicesCount }
            )
        : null,
      servicePoint ? `${mileage}${servicePoint.menuType.description}` : null
    ]
      .filter(v => Boolean(v))
      .join(', ');
    return <div onClick={() => {}}>{title}</div>;
  }

  onServiceSection = () => {
    const { gtmUserEvent } = this.props;
    const headerFlowCmp = this.headerFlow.current.getWrappedInstance();
    gtmUserEvent(
      'Expansion Of List Of Services Section',
      'Clicked',
      'Displayed List',
      'GTM_CURRENT_PAGE > Services Section',
      'Displayed'
    );
    headerFlowCmp.modal.current.setShowModal(true);
  };

  getAdvisor() {
    const { advisors, selectedAdvisorId } = this.props;
    let selectedAdvisor = advisors.find(
      ({ data }) => data === selectedAdvisorId
    );
    selectedAdvisor = selectedAdvisor ? selectedAdvisor.label : '';
    return selectedAdvisor;
  }

  getTransportation() {
    const { transportations, transportationOption, intl } = this.props;
    let selectedTransportationLabel = transportations.find(
      ({ data }) => data === transportationOption
    );
    selectedTransportationLabel = setTransportationLabel(
      selectedTransportationLabel && selectedTransportationLabel.label
        ? selectedTransportationLabel.label
        : {},
      intl
    );
    return selectedTransportationLabel;
  }

  getFinancingOptionMessage() {
    const { intl, isFlexPayEnabled } = this.props;

    // TODO: remove defaultMessage once localization is ready
    return (
      isFlexPayEnabled && (
        <div>
          {intl.formatMessage({
            id: 'c8.review.financing.options',
            defaultMessage:
              'We offer financing options, talk to your service advisor for details.'
          })}
        </div>
      )
    );
  }

  getCtaBackButtonLabel() {
    const { intl, appointmentDate, ctaBackButton } = this.props;
    const parsedDate = parse(appointmentDate.startTime);
    const formattedDate = intl.formatDate(parsedDate, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
    let formattedTime = formatTime(parsedDate, intl, false, false);
    formattedTime = `${formattedDate}, ${formattedTime}`;
    return getCTAbackButtonLabel(
      ctaBackButton,
      formattedTime,
      'c8.header.date.time',
      intl
    );
  }

  getMessageBanner(localization) {
    const {
      intl,
      dealerContact: { servicePhone }
    } = this.props;
    const bannerContent = intl.formatMessage(localization, { servicePhone });
    return <MessageBanner content={bannerContent} />;
  }

  getDisclaimers(disclaimerKeys) {
    return (
      disclaimerKeys.length &&
      disclaimerKeys
        .filter(disc => disc.show)
        .map(disc => (
          <Disclaimer disclaimerInfo={disc} key={disc.disclaimerText.id} />
        ))
    );
  }

  render() {
    const {
      isContactInfoFormValid,
      isFormRemindersValid,
      isLicenseNumberValid,
      isPromotionEnabled,
      totalPrice,
      priceVisibility,
      promoDescription,
      setActivePromotion,
      isApptInPromoInterval,
      goTo,
      appointmentDate,
      contactInfoMissing,
      intl,
      mobilePhoneNumberPopulated,
      vehicle,
      isPromotionInRange,
      externalValet,
      transportationOption,
      appFlow,
      ctaBanner,
      isCarryOver,
      isDmsAppt,
      isQuoteAppt,
      isLoggedIn,
      pickupAddress,
      dropoffAddress,
      isClutchPartialValet,
      dealerContact: { streetAddressOne, city, state, country },
      setRecaptchaToken,
      isRecaptchaEnabledOnCancelAppt,
      languageCode,
      gtmRecaptchaEvent,
      smsSingleOptIn
    } = this.props;
    const isPromoActive =
      isPromotionEnabled &&
      isApptInPromoInterval &&
      isDiscountValid(totalPrice.price, totalPrice.discountedPrice);

    // @TODO: delete defaultMessage later once translations are deployed
    const disclaimerKeys = [
      {
        disclaimerText: {
          id: 'c8.review.book.preferences.disclaimer',
          defaultMessage:
            'Disclaimer: By selecting your Notifications Preferences, you agree to consent to receiving periodic communications, including non-transactional messages, from this dealership. You can opt out at any time. Consent is not required for purchase. Message and data rates may apply.'
        },
        disclaimerTitle: {
          id: 'c8.review.preferences.disclaimer',
          defaultMessage: 'Disclaimer:'
        },
        show: smsSingleOptIn
      },
      {
        disclaimerText: {
          id: 'c8.review.book.disclaimer',
          defaultMessage:
            'DISCLAIMER: Discounts are subject to change without notice, and only one discount per appointment may be honored. Check with our Service Advisor to see if your vehicle may require additional parts and/or labor at an extra charge. Cannot be used with any other specials or like services. Customer is responsible for local tax. Any discounts offered are made by the dealer, who is solely responsible for them. <a href="http://xsupport-tools.com" target="_new">This URL</a>'
        },
        seeMoreButtonText: {
          id: 'c8.appointment.page.time.selector.button.see.more',
          defaultMessage: 'See More'
        },
        disclaimerTitle: {
          id: 'c8.review.disclaimer',
          defaultMessage: 'Disclaimer:'
        },
        modalTitle: { id: 'c8.review.terms', defaultMessage: 'Terms' },
        show: true
      }
    ];
    const activePromoClassName = classNames({
      [`${ns}__info-title--promo-active`]:
        promoDescription && (isPromoActive || isPromotionInRange)
    });

    const isValet =
      transportationOption === VALET || transportationOption === VALETLOANER;
    const isExternalValetAppt = isValet && externalValet;
    const dealerAddress = `${toCapitalize(streetAddressOne)}, ${toCapitalize(
      city
    )}, ${state}, ${country} `;

    let pickupTimeLabel;
    let backButtonAriaLabel = intl.formatMessage({
      id: 'c8.aria.back.to.appointment.time',
      defaultMessage: 'Back to Appointment Time'
    });
    if (isCarryOver) {
      if (isLoggedIn) {
        backButtonAriaLabel = intl.formatMessage({
          id: 'c8.aria.back.to.user.vehicles',
          defaultMessage: 'Back to user vehicle list'
        });
      } else {
        backButtonAriaLabel = intl.formatMessage({
          id: 'c8.aria.back.to.home.page',
          defaultMessage: 'Back to home page'
        });
      }
    }

    if (isExternalValetAppt) {
      pickupTimeLabel = intl.formatMessage(
        {
          id: 'c8.review.pickup.appointment.time',
          defaultMessage: '{date} at {time}'
        },
        {
          date: intl.formatDate(parse(appointmentDate.startTime), {
            month: 'long',
            day: 'numeric'
          }),
          time: formatTime(parse(appointmentDate.startTime), intl, false, false)
        }
      );
    }
    return (
      <AppointmentFlowWizard
        className={ns}
        header={() => {
          return ctaBanner !== null ? (
            <CTAFlowHeader
              ref={this.headerFlow}
              label={isCarryOver ? '' : this.getCtaBackButtonLabel()}
              hideServices={false}
              routeTo={
                isCarryOver
                  ? isLoggedIn
                    ? USER_VEHICLES_ROUTE
                    : HOME_ROUTE
                  : APPOINTMENT_ROUTE
              }
              ctaButtonDisabled={true}
              ctaBanner={ctaBanner}
              onClick={() => {
                mobilePhoneNumberPopulated();
              }}
              typeButton={'cta-next'}
              isBookItButtonVisible={true}
              backButtonAriaLabel={backButtonAriaLabel}
              openModalBookItButton={this.openModal}
              throwErrorBookingBookItButton={this.throwErrorBooking}
              hideBackButton={isDmsAppt}
            />
          ) : (
            <AppointmentFlowHeader
              ref={this.headerFlow}
              label={
                isCarryOver
                  ? ''
                  : intl.formatMessage({ id: 'c8.header.date.time' })
              }
              routeTo={
                isCarryOver
                  ? isLoggedIn
                    ? USER_VEHICLES_ROUTE
                    : HOME_ROUTE
                  : APPOINTMENT_ROUTE
              }
              onClick={() => {
                mobilePhoneNumberPopulated();
              }}
              backButtonAriaLabel={backButtonAriaLabel}
              isDMSAppt={isDmsAppt}
            />
          );
        }}
        footer={() => (
          <Media query={`(max-width: ${APP_BREAKPOINTS.SM - 1}px)`}>
            <BookItButton
              openModal={this.openModal}
              throwErrorBooking={this.throwErrorBooking}
              order={isMobile() ? 2 : 1}
            />
          </Media>
        )}
        body={() => (
          <React.Fragment>
            <Container className={`${ns}__wrapper d-flex flex-column`}>
              {this.buildCancelApptmodal()}
              {this.buildPromoCodeModal()}
              <AppointmentError
                ref={this.errorBooking}
                onClose={() => {
                  goTo(APPOINTMENT_ROUTE, BACKWARD);
                }}
                date={appointmentDate.startTime}
              />
              <Row className="p-0">
                <Col>
                  <h1 className={'page-title'}>
                    {intl.formatMessage({
                      id: 'c8.review.label.review'
                    })}
                  </h1>
                </Col>
              </Row>
              {isDmsAppt &&
                this.getMessageBanner({
                  id: 'c8.review.contact.dealer.modify.appointment.with.phone',
                  defaultMessage:
                    'Contact the dealership if you would like to change your appointment. {servicePhone}'
                })}
              {isQuoteAppt &&
                this.getMessageBanner({
                  id:
                    'c8.review.quote.appt.contact.dealer.to.change.appointment',
                  defaultMessage:
                    'All quoted services will be lost if this appointment is changed online. Please contact the dealership at {servicePhone} if you would like to change your appointment.'
                })}
              <Row id="review_section" className="review-section">
                <Col xs={12} sm={12} md={7}>
                  <Row>
                    <Col className={`${ns}__page-subtitle`}>
                      {intl.formatMessage({
                        id: 'c8.review.contact.info'
                      })}
                    </Col>
                    <Panel className={`${ns}__contact-info-panel mx-2`}>
                      <ContactInfo />
                      <Media query={`(max-width: ${APP_BREAKPOINTS.SM - 1}px)`}>
                        <RemindersAndUpdates
                          isFormRemindersValid={isFormRemindersValid}
                        />
                      </Media>
                    </Panel>
                    {contactInfoMissing &&
                      (!isContactInfoFormValid ||
                        !isFormRemindersValid ||
                        !isLicenseNumberValid) && (
                        <div
                          className={`${ns}__info-missing`}
                          tabIndex={0}
                          aria-label={intl.formatMessage({
                            id: 'c8.review.enter.missing.contact.information'
                          })}
                        >
                          {' '}
                          {intl.formatMessage({
                            id: 'c8.review.enter.missing.contact.information'
                          })}{' '}
                        </div>
                      )}
                  </Row>

                  <Row className={`${ns}__review-panel-row`}>
                    <Col xs={12} className={`${ns}__page-subtitle`}>
                      {intl.formatMessage({
                        id: 'c8.review.label.review'
                      })}
                    </Col>
                    <VehiclePanel
                      vehicleInfo={vehicle}
                      advisor={this.getAdvisor()}
                      transportation={this.getTransportation()}
                      externalValet={isExternalValetAppt}
                      intl={intl}
                    />
                    {isExternalValetAppt && (
                      <Panel className={`${ns}__valet-panel mx-2`}>
                        {this.getValetAddressView(
                          intl.formatMessage({
                            id: 'c8.review.we.will.pickup.your.vehicle',
                            defaultMessage: 'We will pick up your vehicle:'
                          }),
                          pickupAddress,
                          pickupTimeLabel
                        )}
                        {this.getValetAddressView(
                          intl.formatMessage({
                            id: 'c8.review.you.will.pickup.after.service',
                            defaultMessage: 'You will pick up after service:'
                          }),
                          isClutchPartialValet ? dealerAddress : dropoffAddress,
                          ''
                        )}
                      </Panel>
                    )}
                    {appFlow !== QUICK_APPT && (
                      <Panel className={`${ns}__services-panel mx-2`}>
                        {!isPromotionEnabled && (
                          <Container>
                            <Row onClick={() => this.onServiceSection()}>
                              <Col
                                xs={10 - (totalPrice.price <= 0 ? 2 : 0)}
                                md={12 - (totalPrice.price <= 0 ? 2 : 5)}
                                className={`${ns}__info-title`}
                              >
                                {this.getServicesTitle()}
                                <div
                                  className={`${ns}__financing-text d-none d-md-block d-xl-block`}
                                >
                                  {this.getFinancingOptionMessage()}
                                </div>
                              </Col>
                              {totalPrice.price > 0 &&
                                priceVisibility && (
                                  <Col
                                    xs={10}
                                    md={4}
                                    className={`${ns}__info-title--charges`}
                                  >
                                    <Price value={totalPrice.price} />
                                    <span
                                      className={`${ns}__info-text ${ns}__info-text--cursive`}
                                    >
                                      {intl.formatMessage({
                                        id: 'c8.review.estimated.charges'
                                      })}
                                    </span>
                                  </Col>
                                )}
                              <Col
                                xs={2}
                                md={1}
                                className={`${ns}__info-right text-right`}
                                tabIndex={0}
                              >
                                <Icon type="chevron__right" />
                              </Col>
                              <Col className={`d-block d-md-none`}>
                                <div className={`${ns}__financing-text`}>
                                  {this.getFinancingOptionMessage()}
                                </div>
                              </Col>
                            </Row>
                          </Container>
                        )}
                        {isPromotionEnabled && (
                          <Container className={`${ns}__promotion`}>
                            <Row onClick={() => this.onServiceSection()}>
                              <Col
                                xs={10}
                                sm={11}
                                className={`${ns}__info-title`}
                              >
                                {this.getServicesTitle()}
                                <div className={`${ns}__financing-text`}>
                                  {this.getFinancingOptionMessage()}
                                </div>
                              </Col>
                              <Col
                                xs={2}
                                sm={1}
                                className={`${ns}__info-right text-right`}
                                tabIndex={0}
                              >
                                <Icon type="chevron__right" />
                              </Col>
                            </Row>
                            <Row>
                              <Col
                                className={`${ns}__info-title d-flex justify-content-start`}
                              >
                                <div className={activePromoClassName}>
                                  <Price value={totalPrice.price} />
                                </div>
                                {isPromotionInRange && (
                                  <Price
                                    value={
                                      isPromoActive
                                        ? totalPrice.discountedPrice
                                        : totalPrice.price
                                    }
                                    showZero={isPromoActive}
                                  />
                                )}
                              </Col>
                              {isPromotionInRange && (
                                <Col
                                  className={`${ns}__info-right promo-in-range text-right`}
                                >
                                  <Button
                                    buttonTheme="link"
                                    onClick={() => {
                                      setActivePromotion(null);
                                    }}
                                  >
                                    {intl.formatMessage({
                                      id: 'c8.review.promo.remove.promo'
                                    })}
                                  </Button>
                                </Col>
                              )}
                            </Row>
                            {isPromotionInRange && (
                              <Row>
                                <Col className={`${ns}__promotion`}>
                                  <span className={`${ns}__promotion--cursive`}>
                                    {promoDescription}{' '}
                                    {intl.formatMessage({
                                      id: 'c8.review.promo.applied'
                                    })}
                                  </span>
                                </Col>
                              </Row>
                            )}
                            {!isPromotionInRange && (
                              <Row>
                                <Col className={`${ns}__promotion`}>
                                  <span className={`${ns}__promotion--cursive`}>
                                    {intl.formatMessage({
                                      id: 'c8.review.estimated.charges'
                                    })}
                                  </span>
                                </Col>
                                <Col
                                  className={`${ns}__promotion--right text-right`}
                                >
                                  <Button
                                    buttonTheme="link"
                                    onClick={() => {
                                      this.showPromoCodeModal(true);
                                    }}
                                  >
                                    {intl.formatMessage({
                                      id: 'c8.review.promo.enter.promo'
                                    })}
                                  </Button>
                                </Col>
                              </Row>
                            )}
                          </Container>
                        )}
                      </Panel>
                    )}
                  </Row>
                </Col>

                <Media query={`(min-width: ${APP_BREAKPOINTS.SM}px)`}>
                  <Col md={5} id="desktop-size">
                    <Panel className={`${ns}__reminder-panel`}>
                      <div className={`${ns}__book-it-button-container`}>
                        <BookItButton
                          openModal={this.openModal}
                          throwErrorBooking={this.throwErrorBooking}
                          order={2}
                        />
                      </div>
                    </Panel>
                    <AdditionalQuestions />
                    <RemindersAndUpdates
                      isFormRemindersValid={isFormRemindersValid}
                    />
                    {this.getDisclaimers(disclaimerKeys)}
                  </Col>
                </Media>
              </Row>

              <Media query={`(max-width: ${APP_BREAKPOINTS.SM - 1}px)`}>
                <Row>
                  <Col xs={12} id="mobile-size">
                    <AdditionalQuestions />
                    {this.getDisclaimers(disclaimerKeys)}
                  </Col>
                </Row>
              </Media>
              {isRecaptchaEnabledOnCancelAppt && (
                <InvisibleRecaptcha
                  setRecaptchaToken={setRecaptchaToken}
                  languageCode={languageCode}
                  gtmAction={gtmRecaptchaEvent}
                  gtmApi={cancel}
                  order={isMobile() ? 1 : 3}
                  onResolveAction={async () => {
                    this.setState({
                      isListeningForRecaptcha: false
                    });
                    await this.onCancelAppt();
                  }}
                  ref={this.recaptcha}
                />
              )}
            </Container>
          </React.Fragment>
        )}
      />
    );
  }
}

export default ReviewPage;



// WEBPACK FOOTER //
// ./src/components/pages/ReviewPage/ReviewPage.js